<script lang="ts">
  /**
   * BinaryRow — renders a binary string as a row of digit cells with a
   * pointer/highlight on the currently inspected bit.
   *
   * Used by the modular exponentiation visual to show which bit of the
   * exponent is being processed at each step.
   */
  type Props = {
    /** Binary string, MSB first (e.g. "10110" for 22). */
    bits: string;
    /** Index of the highlighted bit, in MSB-first order. -1 = none highlighted. */
    activeIndex?: number;
    /** Indices of bits already processed (will be dimmed). */
    processedIndices?: ReadonlyArray<number>;
  };

  let { bits, activeIndex = -1, processedIndices = [] }: Props = $props();

  const cells = $derived(
    Array.from(bits).map((bit, i) => ({
      bit,
      index: i,
      active: i === activeIndex,
      processed: processedIndices.includes(i),
    }))
  );
</script>

<div class="binary-row">
  <div class="binary-label">Exponent in binary</div>
  <div class="binary-cells">
    {#each cells as cell (cell.index)}
      <div
        class="cell"
        class:active={cell.active}
        class:processed={cell.processed}
        class:one={cell.bit === '1'}
      >
        <span class="cell-bit">{cell.bit}</span>
        {#if cell.active}
          <span class="cell-pointer" aria-hidden="true"></span>
        {/if}
      </div>
    {/each}
  </div>
</div>

<style>
  .binary-row {
    display: flex;
    flex-direction: column;
    gap: var(--space-sm);
    align-items: center;
  }

  .binary-label {
    font-family: var(--font-sans);
    font-size: var(--text-xs);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--ink-muted);
    font-weight: var(--weight-medium);
  }

  .binary-cells {
    display: flex;
    gap: 6px;
  }

  .cell {
    width: 36px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    background: var(--bg);
    color: var(--ink-muted);
    font-family: var(--font-mono);
    font-size: var(--text-lg);
    font-weight: var(--weight-medium);
    position: relative;
    transition:
      border-color 200ms ease,
      background 200ms ease,
      color 200ms ease,
      transform 200ms ease;
  }

  .cell.processed {
    color: var(--ink-subtle);
    background: var(--surface-muted);
  }

  .cell.active {
    border-color: var(--accent);
    background: var(--bg);
    color: var(--ink);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(31, 58, 95, 0.15);
  }

  .cell.one .cell-bit {
    font-weight: var(--weight-bold);
  }

  .cell-pointer {
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 6px solid var(--accent);
  }
</style>
